import React from 'react';
import { isEmpty } from 'lodash';
import { injectIntl } from 'react-intl';
import memoizeOne from 'memoize-one';

import { Circle, GoogleApiWrapper, Map, Marker, Polygon } from '../Gmap';
import './MapOverviewContainer.scss';
import { reverseGeocode } from '../../utils/react';
import { MarkerColors } from '../MapContainer';

const placesAPIKey = process.env.REACT_APP_PLACES_API_KEY;

interface Props {
  center: any;
  google: any;
  history?: any;
  intl: any;
  loading?: boolean;
  locations: any;
  productUrnStrBasket?: string;
  showingProductInfoWindow?: boolean;
  updateLatLngRad?: any;
  zoom?: number;
  onHighlightLocation?: any;
  highlightedLocation?: string;
}

const MapOverviewContainer = ({
  center,
  google,
  locations,
  zoom,
  productUrnStrBasket,
  updateLatLngRad,
  highlightedLocation,
  onHighlightLocation,
}: Props) => {
  const [circles, setCircles] = React.useState({});
  const [showingInfoWindow, setShowingInfoWindow] = React.useState(false);
  const [showingPolygon, setShowingPolygon] = React.useState(false);
  const circleRefs = React.useRef({});

  const onMarkerShowPolygon = (props, marker, isShow = false) => {
    setShowingPolygon(isShow ? marker.name : isShow);
  };

  const onMarkerClick = (props, marker, e, urn, latLng) => {
    setShowingInfoWindow(marker.name);
    onHighlightLocation && onHighlightLocation(urn);
  };

  const onMapClicked = () => {
    if (showingInfoWindow) {
      setShowingInfoWindow(null);
    }
  };

  const getColorCustomMarker = (producturn: string | number) => {
    if (productUrnStrBasket && producturn && productUrnStrBasket.includes(String(producturn))) {
      return MarkerColors.Red;
    }

    if (
      locations[producturn] &&
      (locations[producturn].state !== 0 || (!locations[producturn].state && locations[producturn].state !== 0))
    ) {
      return MarkerColors.Grey;
    }

    if (
      locations[producturn] &&
      locations[producturn].area &&
      !isEmpty(locations[producturn].area) &&
      isEmpty(locations[producturn].subpoints)
    ) {
      return MarkerColors.Yellow;
    }

    return MarkerColors.Green;
  };

  const getPaths = (producturn: string | number, locations: any, areaData: boolean = true) => {
    const area = locations[producturn] && locations[producturn].area ? locations[producturn].area : [];
    const subPoints = locations[producturn] && locations[producturn].subpoints ? locations[producturn].subpoints : [];
    let data = areaData ? area : subPoints;

    if (isEmpty(data) && !isEmpty(subPoints)) {
      data = subPoints;
    }
    let paths = [];
    let subPaths = [];

    if (!isEmpty(data)) {
      const returnCordsObj = ([lng, lat]) => {
        if (lng && lat) {
          return { lat, lng };
        }
      };
      const returnCordsSubpoint = (item) => {
        if (item.x && item.y) {
          return { lat: item.y, lng: item.x };
        }
      };

      const setCords = (item, i) => {
        if (Array.isArray(item[0])) {
          subPaths[i] = [];
          item.forEach((item) => subPaths[i].push(returnCordsObj(item)));
        } else {
          if (item.hasOwnProperty('lng') && item.hasOwnProperty('ltd')) {
            paths.push(returnCordsObj(item));
          }
          if (item.hasOwnProperty('x') && item.hasOwnProperty('y')) {
            paths.push(returnCordsSubpoint(item));
          }
        }
      };

      data.forEach((item, i) => setCords(item, i));
    }

    return areaData && !isEmpty(subPaths) ? subPaths : paths;
  };

  const getPolylineBorder = (keyAddress: string | number, locations: any, areaData: boolean = true) => {
    let data = locations[keyAddress] && locations[keyAddress].border ? locations[keyAddress].border : [];

    let paths = [];
    let subPaths = [];

    if (!isEmpty(data)) {
      const returnCordsObj = ([lng, lat]) => {
        if (lng && lat) {
          return { lat, lng };
        }
      };
      const returnCordsSubpoint = (item) => {
        if (item.x && item.y) {
          return { lat: item.y, lng: item.x };
        }
      };

      const setCords = (item, i) => {
        if (Array.isArray(item[0])) {
          subPaths[i] = [];
          item.forEach((item) => subPaths[i].push(returnCordsObj(item)));
        } else {
          if (item.hasOwnProperty('lng') && item.hasOwnProperty('ltd')) {
            paths.push(returnCordsObj(item));
          }
          if (item.hasOwnProperty('x') && item.hasOwnProperty('y')) {
            paths.push(returnCordsSubpoint(item));
          }
        }
      };

      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      data.forEach((item, i) => setCords(item, i));
    }

    return areaData && !isEmpty(subPaths) ? subPaths : paths;
  };

  // const memoizedGetPaths = memoizeOne(getPaths);
  // const memoizedGetPolyline = memoizeOne(getPolylineBorder);

  const updateLocationRadius = (location: any, lat: number, lng: number, radius: number) => {
    if (!!location && (location.lngLtd.lat != lat || location.lngLtd.lng != lng)) {
      reverseGeocode({ lat, lng })
        .then((res) => {
          const newAddress = res[0]['formatted_address'];
          const types = res[0]['types'];

          updateLatLngRad(lat, lng, radius, location.address, types, newAddress);
        })
        .catch((err) => console.error(err));
    } else if (location.circle.radius != radius) {
      updateLatLngRad(lat, lng, radius, location.address, location.types, null);
    }
  };

  const getZoom = (zoom) => (zoom ? zoom : window.innerWidth > 800 ? 11 : 14);

  return (
    <div className='h-100'>
      <Map
        className='map'
        google={google}
        onClick={onMapClicked}
        zoom={getZoom(zoom)}
        currentMarkerName={showingInfoWindow}
        center={center ? center : undefined}
        initialCenter={center ? center : undefined}
        style={{ width: '100%', height: '100' }}
      >
        {Object.keys(locations).map((keyAddress, idx) => {
          const producturn = locations[keyAddress].producturn;
          const productType = locations[keyAddress].formatcode;
          const address = locations[keyAddress].address;
          const markerContext = locations[keyAddress].context;
          const isHighlighted = locations[keyAddress].urn === highlightedLocation;
          const paths = getPaths(producturn, locations);
          const isExistPolygon = !isEmpty(paths);
          const markerColor = getColorCustomMarker(
            producturn || `${locations[keyAddress].lngLtd.lat}${locations[keyAddress].lngLtd.lng}`,
          );

          return [
            <Marker
              onClick={(props, marker, e) =>
                onMarkerClick(props, marker, e, locations[keyAddress].urn, locations[keyAddress].lngLtd)
              }
              key={keyAddress + 'marker' + idx}
              idx={idx}
              title={address}
              name={producturn || address}
              colorCustomMarker={isHighlighted ? MarkerColors.Red : markerColor}
              selected={isHighlighted}
              useCustomMarker={true}
              useIconsByContext={true}
              context={markerContext}
              productType={productType}
              circle={circles[keyAddress]}
              position={locations[keyAddress].lngLtd}
              onMouseover={isExistPolygon ? (props, marker) => onMarkerShowPolygon(props, marker, true) : null}
              onMouseout={isExistPolygon ? (props, marker) => onMarkerShowPolygon(props, marker) : null}
            />,
            isExistPolygon && showingPolygon === producturn && (
              <Polygon
                paths={paths}
                key={`Polygon${idx}`}
                strokeColor={MarkerColors.Yellow}
                strokeWeight={2}
                fillColor={MarkerColors.Yellow}
                fillOpacity={0.2}
              />
            ),
          ];
        })}

        {/* J2 step 1 */}
        {Object.keys(locations).map((keyAddress, idx) => {
          return locations[keyAddress].types.includes('locality') ? null : (
            <Circle
              key={keyAddress + 'circle'}
              ref={(ref) => (circleRefs[idx] = ref)}
              radius={locations[keyAddress].circle.radius}
              center={locations[keyAddress].lngLtd}
              editable={true}
              draggable
              strokeColor='transparent'
              strokeOpacity={0}
              strokeWeight={5}
              fillColor='#FF0000'
              fillOpacity={0.2}
              onRadiusChanged={() => {
                let radius, lat, lng;

                if (circleRefs && circleRefs[idx] && circleRefs[idx].circle) {
                  radius = circleRefs[idx].circle.radius;
                  lat = circleRefs[idx].circle.center.lat();
                  lng = circleRefs[idx].circle.center.lng();
                }

                updateLocationRadius(locations[keyAddress], lat, lng, radius);
              }}
              onDragend={(props, rad, res) => {
                const {
                  latLng: { lat, lng },
                } = res;
                const { radius } = rad;

                updateLocationRadius(locations[keyAddress], lat(), lng(), radius);
              }}
              setCircle={(circle) => setCircles({ ...circles, [keyAddress]: circle })}
            />
          );
        })}

        {Object.keys(locations).map((keyAddress, idx) => {
          if (locations[keyAddress].context !== 'address') {
            const polylineBorder = getPolylineBorder(keyAddress, locations);
            const isExistPolyLine = !isEmpty(polylineBorder);
            const isHighlighted = locations[keyAddress].urn === highlightedLocation;
            return isExistPolyLine ? (
              <Polygon
                paths={polylineBorder}
                key={`PolygonArea${idx}`}
                strokeColor='#B50200'
                strokeWeight={2}
                fillColor='#FF0000'
                fillOpacity={isHighlighted ? 0.5 : 0.2}
              />
            ) : null;
          } else {
            return null;
          }
        })}
      </Map>
    </div>
  );
};

export default GoogleApiWrapper({ apiKey: placesAPIKey })(injectIntl(MapOverviewContainer));
